import React, { useState, useEffect } from 'react';
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from 'react-i18next';
import {
    List,
    ListItem, ListItemText,
    ListItemSecondaryAction,
    Switch,
    Select,
    MenuItem,
    Slider,
    Box,
    Divider,
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    Typography,
    Paper,
} from '@mui/material';
import {
    LocationOn,
    LocationOff,
} from '@mui/icons-material';
import GooglePlacesAutocomplete from '../GooglePlacesAutocomplete';
import { FONT_FAMILIES } from '../../constants/appConstants';

const GeneralSettings = ({ initialSettings, onSave }) => {
    const { t, i18n } = useTranslation();
    const [settings, setSettings] = useState({
        themeMode: 'light',
        language: 'en',
        fontSize: 14,
        fontFamily: 'system-default',
        ctrlEnterToSend: false,
        userLocation: null,
        showChainOfThought: false,
        alwaysUseThinking: false
    });
    const [showLocationDialog, setShowLocationDialog] = useState(false);
    const [locationSettings, setLocationSettings] = useState({
        country: settings.userLocation?.country || '',
        city: settings.userLocation?.city || '',
        region: settings.userLocation?.region || '',
    });
    const [isMobile, setIsMobile] = useState(false);

    // Update on mobile screens
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 600);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Initialize settings from props
    useEffect(() => {
        if (initialSettings) {
            setSettings(prev => ({
                ...prev,
                ...initialSettings
            }));
        }
    }, [initialSettings]);

    // Set up temp location for dialog
    useEffect(() => {
        setLocationSettings({
            country: settings.userLocation?.country || '',
            city: settings.userLocation?.city || '',
            region: settings.userLocation?.region || '',
            searchInput: settings.userLocation
                ? `${settings.userLocation.city}${settings.userLocation.region ? `, ${settings.userLocation.region}` : ''}, ${settings.userLocation.country}`
                : ''
        });
    }, [settings.userLocation, showLocationDialog]);

    const handleSettingChange = async (setting, value) => {
        const updatedSettings = {
            ...settings,
            [setting]: value
        };

        setSettings(updatedSettings);

        // Special case for language change
        if (setting === 'language') {
            await i18n.changeLanguage(value);
        }

        onSave(updatedSettings);
    };

    const handleClearLocation = async () => {
        const updatedSettings = {
            ...settings,
            userLocation: null
        };
        setSettings(updatedSettings);
        onSave(updatedSettings);
    };

    const handlePlaceSelected = (location) => {
        setLocationSettings({
            country: location.country,
            city: location.city,
            region: location.region,
            searchInput: location.city ? `${location.city}, ${location.region || ''}, ${location.country}`.replace(/, ,/, ',').replace(/^, |, $/, '') : location.formatted_address
        });
    };

    const handleLocationSubmit = async () => {
        const locationToSave = {
            country: locationSettings.country,
            city: locationSettings.city,
            region: locationSettings.region || undefined,
        };

        if (locationToSave.country && locationToSave.city) {
            const updatedSettings = {
                ...settings,
                userLocation: locationToSave
            };

            setSettings(updatedSettings);
            await onSave(updatedSettings);
        }

        setShowLocationDialog(false);
    };

    return (
        <List>
            {/* Theme Mode Setting */}
            <ListItem>
                <ListItemText
                    primary={t('settings.themeMode')}
                    secondary={settings.themeMode === 'dark' ?
                        t('settings.themeDark') :
                        t('settings.themeLight')
                    }
                />
                <ListItemSecondaryAction>
                    <Switch
                        data-testid="theme-mode-switch"
                        edge="end"
                        checked={settings.themeMode === 'dark'}
                        onChange={e => handleSettingChange('themeMode', e.target.checked ? 'dark' : 'light')}
                    />
                </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            {/* Language Setting */}
            <ListItem>
                <ListItemText
                    primary={t('settings.language')}
                    secondary={t(`language.${settings.language}`)}
                />
                <ListItemSecondaryAction>
                    <Select
                        data-testid="language-select"
                        value={settings.language}
                        onChange={e => handleSettingChange('language', e.target.value)}
                        size="small"
                        sx={{ minWidth: 100 }}
                    >
                        <MenuItem value="en">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ReactCountryFlag countryCode="GB" svg />
                                {t('language.en')}
                            </Box>
                        </MenuItem>
                        <MenuItem value="de">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ReactCountryFlag countryCode="DE" svg />
                                {t('language.de')}
                            </Box>
                        </MenuItem>
                        <MenuItem value="ru">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ReactCountryFlag countryCode="RU" svg />
                                {t('language.ru')}
                            </Box>
                        </MenuItem>
                        <MenuItem value="es">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ReactCountryFlag countryCode="ES" svg />
                                {t('language.es')}
                            </Box>
                        </MenuItem>
                    </Select>
                </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            {/* Font Family Setting */}
            <ListItem>
                <ListItemText
                    primary={t('settings.fontFamily')}
                    secondary={FONT_FAMILIES.find(f => f.id === settings.fontFamily)?.name || t('settings.systemDefault')}
                />
                <ListItemSecondaryAction>
                    <Select
                        data-testid="font-family-select"
                        value={settings.fontFamily}
                        onChange={e => handleSettingChange('fontFamily', e.target.value)}
                        size="small"
                        sx={{ minWidth: 150 }}
                    >
                        {FONT_FAMILIES.map(font => (
                            <MenuItem
                                key={font.id}
                                value={font.id}
                                sx={{ fontFamily: font.value }}
                            >
                                {t(`settings.fonts.${font.id}`)}
                            </MenuItem>
                        ))}
                    </Select>
                </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            {/* Font Size Setting */}
            <ListItem>
                <ListItemText
                    primary={t('settings.fontSize')}
                    secondary={`${settings.fontSize}px`}
                />
                <ListItemSecondaryAction sx={{ width: '150px' }}>
                    <Slider
                        data-testid="font-size-slider"
                        value={settings.fontSize}
                        min={10}
                        max={20}
                        step={2}
                        onChange={(_, value) => handleSettingChange('fontSize', value)}
                        marks
                        size="small"
                    />
                </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            {/* Enter Key Behavior Setting */}
            <ListItem>
                <ListItemText
                    primary={t('settings.enterBehavior')}
                    secondary={settings.ctrlEnterToSend ?
                        t('settings.ctrlEnterToSend') :
                        t('settings.enterToSend')
                    }
                />
                <ListItemSecondaryAction>
                    <Switch
                        data-testid="enter-behavior-switch"
                        edge="end"
                        checked={settings.ctrlEnterToSend}
                        onChange={e => handleSettingChange('ctrlEnterToSend', e.target.checked)}
                    />
                </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            {/* Chain of Thought Display Setting */}
            <ListItem>
                <ListItemText
                    primary={t('settings.showChainOfThought')}
                    secondary={settings.showChainOfThought ?
                        t('settings.showChainOfThoughtEnabled') :
                        t('settings.showChainOfThoughtDisabled')
                    }
                />
                <ListItemSecondaryAction>
                    <Switch
                        data-testid="chain-of-thought-switch"
                        edge="end"
                        checked={settings.showChainOfThought}
                        onChange={e => handleSettingChange('showChainOfThought', e.target.checked)}
                    />
                </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            {/* Always Use Thinking Setting */}
            <ListItem>
                <ListItemText
                    primary={t('input.alwaysUseThinking')}
                    secondary={settings.alwaysUseThinking ?
                        t('settings.alwaysUseThinkingEnabled') :
                        t('settings.alwaysUseThinkingDisabled')
                    }
                />
                <ListItemSecondaryAction>
                    <Switch
                        data-testid="always-use-thinking-switch"
                        edge="end"
                        checked={settings.alwaysUseThinking}
                        onChange={e => handleSettingChange('alwaysUseThinking', e.target.checked)}
                    />
                </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            {/* User Location Setting */}
            <ListItem
                sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    ...(isMobile && { minHeight: 120 })
                }}
            >
                <ListItemText
                    primary={t('settings.userLocation')}
                    secondary={settings.userLocation ?
                        `${settings.userLocation.city}${settings.userLocation.region ? `, ${settings.userLocation.region}` : ''}, ${settings.userLocation.country}` :
                        t('settings.userLocationNotSet')
                    }
                    sx={{ width: '100%' }}
                />
                {isMobile ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        width: '100%',
                        mt: 2
                    }}>
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            startIcon={<LocationOn />}
                            onClick={() => setShowLocationDialog(true)}
                        >
                            {settings.userLocation ? t('settings.changeLocation') : t('settings.setLocation')}
                        </Button>
                        {settings.userLocation && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                fullWidth
                                startIcon={<LocationOff />}
                                onClick={() => handleClearLocation()}
                            >
                                {t('settings.clearLocation')}
                            </Button>
                        )}
                    </Box>
                ) : (
                    <ListItemSecondaryAction>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <Button
                                data-testid="location-button"
                                variant="outlined"
                                size="small"
                                startIcon={<LocationOn />}
                                onClick={() => setShowLocationDialog(true)}
                            >
                                {settings.userLocation ? t('settings.changeLocation') : t('settings.setLocation')}
                            </Button>
                            {settings.userLocation && (
                                <Button
                                    data-testid="clear-location-button"
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<LocationOff />}
                                    onClick={() => handleClearLocation()}
                                >
                                    {t('settings.clearLocation')}
                                </Button>
                            )}
                        </Box>
                    </ListItemSecondaryAction>
                )}
            </ListItem>
            {/* Location Dialog */}
            <Dialog open={showLocationDialog} onClose={() => setShowLocationDialog(false)}>
                <DialogTitle>{t('settings.setLocationTitle')}</DialogTitle>
                <DialogContent>
                    <Typography paragraph color="text.secondary">
                        {t('settings.locationDescription')}
                    </Typography>
                    <GooglePlacesAutocomplete
                        onPlaceSelected={handlePlaceSelected}
                        placeholder={t('settings.searchLocation')}
                        helperText={t('settings.searchLocationHelp')}
                    />
                    {locationSettings.city && (
                        <Paper sx={{ p: 2, mt: 2, backgroundColor: 'background.default' }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('settings.selectedLocation')}:
                            </Typography>
                            <Typography>
                                {`${locationSettings.city}${locationSettings.region ? `, ${locationSettings.region}` : ''}, ${locationSettings.country}`}
                            </Typography>
                        </Paper>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowLocationDialog(false)}>{t('cancel')}</Button>
                    <Button
                        data-testid="save-location-button"
                        onClick={handleLocationSubmit}
                        variant="contained"
                        color="primary"
                        disabled={!locationSettings.country || !locationSettings.city}
                    >
                        {t('save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </List>
    );
};

export default GeneralSettings;